import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { PassportMainWithDraftDto } from '../interfaces/api-responses';
import { PassportService, PassportRecord } from './passport.service';
import { PassportDetailsStore } from './passport-details.store';
import { PassportHelperService } from './passport-helper.service';
import { DateTimeHelperService } from './date-time-helper.service';

@Injectable({ providedIn: 'root' })
export class PassportsDataService {
  private isLoading = false;

  constructor(
    private readonly http: HttpClient,
    private readonly passports: PassportService,
    private readonly detailsStore: PassportDetailsStore,
    private readonly helper: PassportHelperService,
    private readonly dateTimeHelper: DateTimeHelperService
  ) {}

  /**
   * Get persona ID based on persona/role name
   * Based on database setup:
   * PersonaID | PersonaName | RoleID
   * 1         | Admin       | 4
   * 2         | Grower      | 4
   * 3         | Transloader | 4
   * 4         | Receiver    | 4
   * 5         | Shunter     | 4
   * 6         | Scheduler   | 4
   * 7         | Gatehouse   | 4
   * 
   * Using PersonaID as the roleId parameter for API calls
   */

  async preloadForCurrentUser(): Promise<void> {
    if (this.isLoading) {
      console.log('Passport preload already in progress, skipping...');
      return;
    }
    this.isLoading = true;
    
    try {
      const userID = localStorage.getItem('userID');
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      const persona = localStorage.getItem('persona') || '';

      // Audit persona uses its own criteria-based search endpoint, not the
      // generic user+role endpoint which applies plant-based filtering.
      // Preloading via the generic endpoint would either return an empty set
      // (roleId 8 is not handled) or stale plant-scoped data from a previous
      // persona, so skip preloading entirely for audit.
      if (persona.toLowerCase().includes('audit')) {
        console.log('Skipping passport preload for Audit persona (uses dedicated search endpoint)');
        return;
      }
      
      console.log('Starting passport preload with:', {
        userID,
        hasToken: !!token,
        tokenType,
        persona
      });
      
      if (!token || !userID) {
        console.warn('Missing authentication data for passport preload:', { hasToken: !!token, hasUserID: !!userID });
        return;
      }

      // Get appropriate role ID for the user's persona
      const roleId = this.helper.getRoleIdForPersona(persona);
      console.log(`Preloading passports for user ${userID}, persona '${persona}', using roleId ${roleId}`);

      const apiUrl = `${environment.apiBaseUrl}/api/v1/passports?userId=${userID}&roleId=${roleId}`;
      const headers = { 
        'Authorization': `${tokenType} ${token}`, 
        'Content-Type': 'application/json' 
      };
      
      console.log('Making API call to:', apiUrl);
      // API returns List<PassportMainWithDraftDto> directly
      const passports = await firstValueFrom(this.http.get<PassportMainWithDraftDto[]>(apiUrl, { headers }));
      console.log('API Response received:', {
        passportsCount: passports?.length || 0,
        firstPassport: passports?.[0]
      });

      if (passports && Array.isArray(passports)) {
        this.storePassportList(passports);
        console.log(`Successfully stored ${passports.length} passports`);
      } else {
        console.warn('Invalid response format:', passports);
      }
    } catch (error: any) {
      // Log detailed error for debugging
      console.warn('Failed to preload passports:', error);
      
      if (error?.status === 401) {
        console.warn('Authentication failed - token may be invalid');
      } else if (error?.status === 403) {
        console.warn('Permission denied - user may not have access to this data');
      } else if (error?.status === 500) {
        console.warn('Server error - backend service may be unavailable');
      }
      
      // Clear any existing data on error
      this.passports.records.set([]);
    } finally {
      this.isLoading = false;
    }
  }

  private storePassportList(passports: PassportMainWithDraftDto[]): void {
    // Store list with basic status (we'll need to determine status from other fields)
    const frontendRecords: PassportRecord[] = passports.map(passport => ({
      id: passport.passportID.toString(),
      status: 'pending', // Default to pending, update based on actual business logic
      isSynced: true
    }));
    this.passports.records.set(frontendRecords);

    // Store details
    for (const passport of passports) {
      try {
        this.detailsStore.upsert({
          id: passport.passportID.toString(),
          loadNumber: passport.loadNumber || '',
          dateTime: this.dateTimeHelper.ensureUtcTimestamp(passport.dueDateTime || passport.scheduledDateTime || ''),
          dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(passport.dueDateTime || passport.scheduledDateTime || ''),
          vendorName: passport.vendorName || '',
          vendorNumber: passport.vendorNumber || '',
          apnName: passport.apnName || '',
          variety: passport.variety || '',
          haulier: passport.haulierName || '',
          destination: passport.destination || '',
          destinationWeighbridge: passport.destinationWeighbridge || '',
          origin: this.helper.formatOriginWithType(passport.originName, passport.rawSourceType),
          loadingDateTime: this.dateTimeHelper.ensureUtcTimestamp(passport.loadingDateTime || ''),
          bulkerNumber: passport.trailerNumber || '',
          bulkerMain: passport.trailerMain || '',
          haccpCheck: (passport.haccpCheck === true ? 'Yes' : 'No'),
          haccpImages: [],
          authorisedBy: passport.authorizerName || '',
          plannedDateTime: this.dateTimeHelper.ensureUtcTimestamp(passport.plannedDateTime || '')
        });
      } catch (e) {
        console.warn(`Failed to upsert details for passport ${passport.passportID}:`, e);
      }
    }
  }
}


